import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft, Check, Phone, Mail } from 'lucide-react';
import { useLang, ui } from '../i18n/LanguageContext';
import { services, categories, contactInfo } from '../mock/data';
import useReveal from '../hooks/useReveal';

const ServiceDetail = () => {
  const { slug } = useParams();
  const { t } = useLang();
  useReveal();

  const service = services.find((s) => s.slug === slug);
  if (!service) {
    return (
      <div className="pt-40 pb-32 text-center">
        <p className="font-serif text-3xl mb-6">404</p>
        <Link to="/services" className="link-underline text-sm">{t(ui.cta.backToServices)}</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === service.category);
  const related = services.filter((s) => s.category === service.category && s.slug !== service.slug).slice(0, 3);
  const list = service.list ? t(service.list) : null;

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 bg-[#161514] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={service.image} alt="" className="w-full h-full object-cover grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#161514] via-[#161514]/60 to-[#161514]/40" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/70 hover:text-champagne text-sm mb-6 transition-colors">
            <ArrowLeft size={16} /> {t(ui.cta.backToServices)}
          </Link>
          <p className="eyebrow text-champagne mb-4 fade-in-up">{t(category.short)}</p>
          <h1 className="font-serif text-white text-4xl md:text-6xl leading-[1.08] max-w-3xl fade-in-up">{t(service.title)}</h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-8 reveal">
            <p className="font-serif text-2xl md:text-3xl text-[#161514] leading-snug mb-10">{t(service.excerpt)}</p>
            <div className="space-y-6 text-lg text-[#3a3833] leading-relaxed">
              {t(service.content).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {list && (
              <div className="mt-12 bg-secondary p-8 md:p-10">
                <p className="eyebrow text-champagne mb-6">{service.listTitle ? t(service.listTitle) : t(ui.services.tag)}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                  {list.map((item, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <Check size={19} className="text-champagne shrink-0 mt-1" strokeWidth={1.6} />
                      <span className="text-[#3a3833]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {service.content2 && (
              <div className="space-y-6 text-lg text-[#3a3833] leading-relaxed mt-12">
                {t(service.content2).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}

            {service.sections && t(service.sections).map((sec, si) => (
              <div key={si} className="mt-12">
                <h2 className="font-serif text-2xl md:text-3xl text-[#161514] mb-5">{sec.heading}</h2>
                <div className="space-y-6 text-lg text-[#3a3833] leading-relaxed">
                  {sec.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            ))}

            {service.callout && (
              <div className="mt-14 border-l-2 border-champagne bg-secondary p-8 md:p-10">
                <h2 className="font-serif text-2xl md:text-3xl text-[#161514] mb-5">{t(service.callout).heading}</h2>
                <div className="space-y-5 text-lg text-[#3a3833] leading-relaxed">
                  {t(service.callout).paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 reveal">
            <div className="bg-[#161514] text-white p-8 lg:sticky lg:top-28">
              <h3 className="font-serif text-2xl mb-4">{t(ui.cta.bookConsult)}</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-7">{t(ui.contact.intro)}</p>
              <div className="space-y-4 text-sm">
                <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 hover:text-champagne transition-colors">
                  <Phone size={17} className="text-champagne" /> {contactInfo.phone}
                </a>
                <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 hover:text-champagne transition-colors">
                  <Mail size={17} className="text-champagne" /> {contactInfo.email}
                </a>
              </div>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 w-full mt-8 bg-white text-[#161514] px-6 py-3.5 text-sm hover:bg-champagne hover:text-white transition-all duration-300">
                {t(ui.cta.contact)} <ArrowUpRight size={16} />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-secondary py-20 md:py-24">
          <div className="max-w-[1280px] mx-auto px-5 md:px-8">
            <h2 className="font-serif text-2xl md:text-3xl mb-10 reveal">{t(ui.services.relatedTitle)}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {related.map((s, i) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="group bg-[#FBFAF8] hover-lift reveal" style={{ transitionDelay: `${i * 90}ms` }}>
                  <div className="img-zoom aspect-[4/3]">
                    <img src={s.image} alt={t(s.title)} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-lg mb-2 group-hover:text-champagne transition-colors">{t(s.title)}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t(s.excerpt)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ServiceDetail;
